import { useCallback, useEffect, useMemo, useRef, useState, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { UserRound, Filter, RefreshCw, X, Footprints, LayoutGrid, Map as MapIcon } from "lucide-react";
import AppShell from "../shell/AppShell.jsx";
import DetailDrawer from "../components/DetailDrawer.jsx";
import GoogleMiniMap, { googleMapsSearchUrl } from "../components/GoogleMiniMap.jsx";
import { formatServerDateTimeIct } from "../components/serverTime.js";
import FootstepDialog from "./FootstepDialog.jsx";
import { useBackofficeIdentity } from "../hierarchy/BackofficeIdentityContext.jsx";
import { useRuntimeConfig } from "../config/RuntimeConfigContext.jsx";
import {
  useWarRoomWorkers,
  WAR_ROOM_DEFAULT_PAGE_SIZE,
  WAR_ROOM_PAGE_SIZE_OPTIONS,
  WAR_ROOM_DEFAULT_SORT_BY,
  WAR_ROOM_DEFAULT_SORT_DIRECTION,
} from "./useWarRoomWorkers.js";
import { getWarRoomPersonDetail } from "./warRoomApi.js";
import { getWorkerProfile } from "../workerProfiles/workerProfilesApi.js";
import { getWorkerApplication, getWorkerApplicationPassportDocumentBlob } from "../workerApplications/workerApplicationsApi.js";
import "./warRoom.css";
import "../pages/states.css";
import Pagination from "../components/Pagination.jsx";

import PageSkeleton, { DetailSkeleton, MediaSkeleton } from "../components/Skeleton.jsx";

// The map view pulls in maplibre-gl. Lazy, exactly like FootstepHistoryPage
// and the Footstep dialog load RouteMap, so an operator who only ever reads
// the seating board never downloads a map engine.
const WarRoomMapPanel = lazy(() => import("./WarRoomMapPanel.jsx"));

const CRUMBS = ["Backoffice", "War Room"];
const DRAWER_TITLE_ID = "wr-drawer-title";
const SEARCH_DEBOUNCE_MS = 350;
const ALL_SCOPE_LABEL = "แรงงานทั้งหมดใน Scope";

// Prompt 141R14 Part B: these must stay in sync with the matching px values
// in warRoom.css (.wx-wr-seat width/height, .wx-wr-seats gap, .wx-wr-row-label
// width) - there is deliberately no single shared source of truth beyond
// this comment, the same convention the old SEATS_PER_ROW constant already
// used (a plain JS constant, not a CSS custom property read at runtime).
const BOARD_SEAT_WIDTH_PX = 28;
const BOARD_SEAT_GAP_PX = 2;
// The row label (24px) plus the real flex `gap` between the label and the
// seats container (.wx-wr-seat-row's own `gap: 4px`) - folded into one
// constant because both are real, unavoidable width the row spends before
// any seat can use it, matching the ticket's own formula shape (`available -
// rowLabelWidth`) as closely as the real markup allows.
const BOARD_ROW_LABEL_WIDTH_PX = 28;
// The board body's own right padding (.wx-wr-board-body--readable) - real,
// unavoidable space. Prompt 141R11's version of this constant also reserved
// a full aisle-divider width "just in case" a row needed one; Prompt 141R14
// removed the aisle-divider concept entirely (see the removed
// aisleGroupSizeForColumns/.wx-wr-aisle) specifically because that
// reservation was the single biggest source of the still-visible right-side
// gutter the owner rejected this packet for - seats now run continuously
// across the row with no internal break to budget for.
const BOARD_BODY_PADDING_RIGHT_PX = 4;
const BOARD_MIN_COLUMNS = 8;

/**
 * Pure, unit-tested board-width calculation (Prompt 141R11/141R14 Part A/B):
 * given the board body's real measured content width, returns how many
 * seats fit per visual row - never a fixed constant that ignores the actual
 * viewport, the exact reason the old SEATS_PER_ROW=22 left a visible
 * right-side gutter on wide screens. `overrides` exists purely so this
 * function is testable against explicit numbers without needing a real
 * DOM/ResizeObserver.
 */
export function computeSeatColumns(containerWidth, overrides = {}) {
  const {
    seatWidth = BOARD_SEAT_WIDTH_PX,
    seatGap = BOARD_SEAT_GAP_PX,
    rowLabelWidth = BOARD_ROW_LABEL_WIDTH_PX,
    reservedWidth = BOARD_BODY_PADDING_RIGHT_PX,
    minColumns = BOARD_MIN_COLUMNS,
  } = overrides;

  if (!Number.isFinite(containerWidth) || containerWidth <= 0) {
    return minColumns;
  }

  const availableWidth = containerWidth - rowLabelWidth - reservedWidth;
  const columns = Math.floor((availableWidth + seatGap) / (seatWidth + seatGap));
  return Math.max(minColumns, columns);
}

/**
 * Measures a container's real rendered content width via ResizeObserver so
 * the seating board can compute how many seats actually fit per row instead
 * of guessing with a fixed constant. Returns a CALLBACK ref, not a plain
 * ref - deliberately: WarRoomPage always renders a loading state (a
 * completely different element tree, isInitialLoading) before the board
 * ever mounts, so the board body div doesn't exist on this component's
 * first render/commit. A plain `useRef` + `useEffect(..., [ref])` only ever
 * fires once, on that very first commit, when `ref.current` is still null -
 * it would never re-measure once the board actually mounts on a later
 * render. A callback ref fires every time React attaches/detaches the DOM
 * node, on whichever render that happens, so this hook fixed a real "the
 * board always renders at the minimum column count" bug caught in this
 * packet's own acceptance screenshots before it ever shipped. Falls back to
 * a one-time getBoundingClientRect measurement (still real, just not
 * live-updating) in environments without ResizeObserver (e.g. jsdom in
 * tests) - computeSeatColumns already treats a 0/unmeasured width as "use
 * the safe minimum", so tests render a real, if narrower, board rather than
 * crashing.
 */
function useContainerWidth() {
  const [width, setWidth] = useState(0);
  const observerRef = useRef(null);

  const setRef = useCallback((element) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    if (!element) {
      return;
    }

    setWidth(element.getBoundingClientRect().width);

    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setWidth(entry.contentRect.width);
      }
    });
    observer.observe(element);
    observerRef.current = observer;
  }, []);

  return [setRef, width];
}

const STATUS_CATEGORY_OPTIONS = ["normal", "warning", "abnormal", "unknown"];
const STATUS_CATEGORY_LABEL_TH = { normal: "Normal", warning: "Warning", abnormal: "Abnormal", unknown: "Unknown" };

// Prompt 141R9: "ขั้นตอนแรงงาน" (worker stage) is a single, operator-facing
// simplification of the two real underlying dimensions (applicationStatus/
// workerStatus) - it deliberately collapses EkycInProgress/EkycApproved/
// EkycRejected/EkycNeedsCorrection into one "e-KYC" choice, since an
// enterprise filter drawer is not the place for five near-identical raw
// enum values. "Worker Profile" maps to workerStatus=ProfileCreated (the
// person has no applicationStatus at all once promoted) - the two real
// filter fields stay mutually exclusive, exactly like the data itself
// (a person is ever a WorkerApplication OR a WorkerProfile, never both).
const WORKER_STAGE_OPTIONS = [
  { value: "", label: "ทั้งหมด" },
  { value: "QuickRegistered", label: "Quick Registered" },
  { value: "EkycInProgress", label: "e-KYC" },
  { value: "WorkerProfile", label: "Worker Profile" },
];

// Prompt 141R9: sortBy/sortDirection are bundled as one operator-facing
// choice - each option already implies its own natural direction, so the
// drawer never needs a separate asc/desc toggle the owner never asked for.
const SORT_OPTIONS = [
  { value: "name", label: "ชื่อ A-Z", sortBy: "name", sortDirection: "asc" },
  { value: "status", label: "สถานะก่อน", sortBy: "status", sortDirection: "asc" },
  { value: "recent", label: "ล่าสุด", sortBy: "recent", sortDirection: "desc" },
];

// Updated with the map view: the old copy ended "ระบบยังไม่มีแผนที่" (there is
// no map yet), which stopped being true the moment this packet shipped one.
// The rest of the sentence stays as it was, including the automatic-alerting
// gap, which is still real.
const ABOUT_TOOLTIP =
  "บอร์ดนี้รวมแรงงานทุกคนในสายงานของคุณ ไม่ใช่เฉพาะคนที่เครื่องส่งสัญญาณแล้ว คนที่ยังไม่เคยส่งสัญญาณจะขึ้นว่า Unknown ไม่ใช่หายไปจากบอร์ด เรียงตามชื่อทั้งหมดเสมอ ไม่ใช่เรียงเฉพาะหน้าที่เปิดอยู่ มุมมองแผนที่จะวางหมุดเฉพาะคนที่เครื่องเคยรายงานตำแหน่ง คนที่ยังไม่เคยรายงานจะไม่มีหมุดและระบบจะไม่เดาตำแหน่งให้ ระบบยังไม่แจ้งเตือนให้อัตโนมัติ";

// The two ways to read the same scoped roster. The map opens first (owner's
// call): it is the view that answers "where is everyone right now", which is
// what this room is for. The board is one click away and remains the complete
// list - it holds every person, including everyone the map structurally
// cannot place, and the map states that count on screen so switching to the
// board is an informed choice rather than a discovery.
const DEFAULT_VIEW_MODE = "map";
const VIEW_MODES = [
  { value: "map", label: "แผนที่", Icon: MapIcon },
  { value: "board", label: "บอร์ดที่นั่ง", Icon: LayoutGrid },
];

// P141R15: the drawer no longer sends an operator to the Missing Heartbeat
// page at all - that domain's own reference/time/status now renders inline
// (see the "เหตุ Missing Heartbeat ที่เกี่ยวข้อง" section below), so a route
// jump would just be a slower, redundant path to information already on
// screen. SOS keeps its own real cross-link: SOS Cases is a materially
// different, actively-managed case workflow this packet does not attempt to
// bring inline, and the owner's removal instruction named Missing Heartbeat
// specifically, not SOS.
function sourceDestination(sourceType) {
  return sourceType === "Sos" ? { label: "เปิดหน้า SOS Cases", to: "/backoffice/sos/cases" } : null;
}

// P141R15: formal, enterprise-register copy replacing the old casual
// "แดงเพราะอะไร" heading/body - still built entirely from the real detail
// fields (sourceType/reasonLabel), never a freeform/fabricated sentence.
function formalAbnormalReasonText(detail) {
  if (detail.sourceType === "Sos") {
    return "ระบบจัดสถานะเป็นผิดปกติ เนื่องจากพบเคส SOS ที่ยังไม่ถูกปิดรายการ";
  }
  if (detail.sourceType === "AbnormalEvent") {
    return "ระบบจัดสถานะเป็นผิดปกติเนื่องจากพบเหตุ Missing Heartbeat ที่ยังไม่ถูกปิดรายการ";
  }
  return detail.reasonLabel;
}

function heartbeatBadgeVariant(statusCategory) {
  return { normal: "success", warning: "warning", abnormal: "danger", unknown: "neutral" }[statusCategory] ?? "neutral";
}

// P144-R2: real, whichever lifecycle field this personType actually carries
// (the other one is always null by construction - never showing both avoids
// the "ไม่มี (เป็น Worker Profile แล้ว)" always-present noise row the old
// drawer had).
function lifecycleStatusValue(detail) {
  return detail.personType === "WorkerProfile" ? detail.workerStatus : detail.applicationStatus;
}

// P144-R2: a compact reference for the drawer header/alert band - short
// values (this app's own ids, and every test fixture) pass through
// untouched; only a genuinely long id (e.g. a real 36-char "abn_"+guid32hex
// AbnormalEvent id) gets shortened. The full value always still lives in the
// "รายละเอียดเชิงเทคนิค" collapsed section - this only controls what's
// visible in the main, always-open part of the drawer.
function truncateId(id, headLen = 10, tailLen = 6) {
  if (!id) {
    return "-";
  }
  if (id.length <= headLen + tailLen + 1) {
    return id;
  }
  return `${id.slice(0, headLen)}…${id.slice(-tailLen)}`;
}

/**
 * Real dedup by the stable key the owner asked for (sourceType + sourceId) -
 * a plain Map so a later value for the same key replaces the earlier one
 * rather than appending a second entry. Exported (same convention as
 * computeSeatColumns/buildAlphabeticalSeatRows below) so this can be unit
 * tested directly against a synthetic candidate list, independent of
 * whatever the real WarRoomPersonDetailDto happens to carry today.
 */
export function dedupeSignals(candidates) {
  const seen = new Map();
  for (const candidate of candidates) {
    if (!candidate?.sourceType || !candidate?.sourceId) {
      continue;
    }
    seen.set(`${candidate.sourceType}:${candidate.sourceId}`, candidate);
  }
  return [...seen.values()];
}

// P144-R2: WarRoomPersonDetailDto only ever carries ONE real, server-
// resolved source today (see WarRoomService.ComputeStatus's own SOS-before-
// AbnormalEvent precedence) - this always yields 0 or 1 real signal, never a
// fabricated one. dedupeSignals still runs over it (rather than building the
// array by hand) so this stays correct if a future packet ever adds a second
// real source field to the DTO.
export function buildDrawerSignals(detail) {
  return dedupeSignals([
    detail.sourceType && detail.sourceId
      ? { sourceType: detail.sourceType, sourceId: detail.sourceId, label: detail.sourceType === "Sos" ? "SOS" : "Missing Heartbeat" }
      : null,
  ]);
}

function describeListError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์เข้าถึง War Room";
  }
  return "กรุณาลองรีเฟรชหน้านี้อีกครั้ง หากยังพบปัญหากรุณาติดต่อฝ่ายปฏิบัติการ";
}

function describeDetailError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ดูสถานะของแรงงานรายนี้";
  }
  if (error?.status === 404) {
    return "ไม่พบข้อมูลแรงงานรายนี้";
  }
  return "ไม่สามารถโหลดรายละเอียดได้ กรุณาลองใหม่อีกครั้ง";
}

/**
 * Every value this formats (lastHeartbeatAtUtc, lastLocationAtUtc,
 * sourceEventAtUtc) is a designator-less backend UTC timestamp, so it must go
 * through the shared normalizer - the local copy this replaced parsed those as
 * browser-local AND formatted in the machine's own zone, printing a time up to
 * 7 hours off under a literal "ICT" suffix. See components/serverTime.js.
 */
function formatDateTimeTh(value) {
  return formatServerDateTimeIct(value);
}

/**
 * Deliberately NOT the shared helper: this one is handed a browser-side
 * `new Date()` (the moment the board last refreshed), not a server timestamp,
 * so the operator's own clock is the correct zone to show it in.
 */
function formatTimeOnlyTh(date) {
  if (!date) {
    return null;
  }
  return date.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
}

/** Splits a bucket's people into fixed-size theater "seat lines" so a large bucket still wraps into several visual rows, never a single unbroken strip. */
function chunkIntoRows(items, rowSize) {
  const rows = [];
  for (let i = 0; i < items.length; i += rowSize) {
    rows.push(items.slice(i, i + rowSize));
  }
  return rows;
}

const THAI_CHAR_PATTERN = /[฀-๿]/;
const LATIN_LETTER_PATTERN = /[A-Za-z]/;

/**
 * Prompt 141R9's whole reason for existing: the seating board's row label
 * must be a real first-letter bucket of the person's own displayName, never
 * an arbitrary page-chunk index. A Latin name buckets to its uppercased
 * first letter (A-Z); a Thai name buckets to its own first Thai character
 * (not folded into a single generic "Thai" bucket, so ก/ข/ค.../ฮ each read
 * as their own real row, the same way A/B/C do); a blank/number/symbol-led
 * name buckets to "#".
 */
function bucketLabelForName(displayName) {
  const trimmed = (displayName ?? "").trim();
  if (!trimmed) {
    return "#";
  }
  const firstChar = trimmed[0];
  if (THAI_CHAR_PATTERN.test(firstChar)) {
    return firstChar;
  }
  if (LATIN_LETTER_PATTERN.test(firstChar)) {
    return firstChar.toUpperCase();
  }
  return "#";
}

/**
 * Pure helper (exported for its own unit test) that turns the current
 * page's items into real alphabetical seating buckets. Sorting here with
 * Intl.Collator(["th","en"], {sensitivity:"base", numeric:true}) is a
 * visual-safety net only - the real, global (cross-page) ordering already
 * comes from the server (WarRoomRepository's `DisplayName COLLATE
 * Thai_CI_AS` ORDER BY, Prompt 141R9) - this local re-sort just guarantees
 * the current page's own bucket grouping is internally consistent even if
 * a caller ever renders this helper against unsorted data (e.g. a test
 * fixture). "#" always sorts last, regardless of where Intl.Collator would
 * otherwise place blank/symbol/number-led names, matching the same
 * "unknown things go last" convention a phone contacts app uses.
 */
export function buildAlphabeticalSeatRows(items) {
  const collator = new Intl.Collator(["th", "en"], { sensitivity: "base", numeric: true });
  const sorted = [...items].sort((a, b) => collator.compare(a.displayName ?? "", b.displayName ?? ""));

  const order = [];
  const byLabel = new Map();
  for (const item of sorted) {
    const label = bucketLabelForName(item.displayName);
    if (!byLabel.has(label)) {
      byLabel.set(label, []);
      order.push(label);
    }
    byLabel.get(label).push(item);
  }

  order.sort((a, b) => {
    if (a === "#" && b !== "#") return 1;
    if (b === "#" && a !== "#") return -1;
    return collator.compare(a, b);
  });

  return order.map((label) => ({ label, items: byLabel.get(label) }));
}

/**
 * /backoffice/war-room - the War Room command board, sourced from the real
 * scoped roster (GET .../war-room/workers) instead of Latest Tracking.
 * Every person in the caller's scope appears here - Quick Registered
 * applicants, e-KYC-in-progress applicants, and every promoted
 * WorkerProfileCreated worker - regardless of whether they have ever sent a
 * heartbeat. e-KYC/Ready for Match/Tracking are statuses ON a seat, never a
 * gate on whether that seat exists at all; a person with no tracking data
 * renders as an Unknown/gray seat with an honest reason label ("รอ e-KYC",
 * "e-KYC กำลังดำเนินการ", "ยังไม่เริ่ม Tracking"), never an empty board.
 *
 * The board defaults to ONE flat "แรงงานทั้งหมดใน Scope" list - it never
 * groups by Agent by default (Agent is a narrowing filter only, same as
 * Super Agent Org/PLC/Application Status/Worker Status). Seating rows
 * (Prompt 141R9) are real alphabetical buckets of displayName - see
 * buildAlphabeticalSeatRows - never a page-chunk index; the server itself
 * sorts the whole scoped result by DisplayName by default
 * (WarRoomService.NormalizeSortBy), so page boundaries land at real
 * alphabetical seams too, not just this page's own local re-sort.
 *
 * Header/toolbar (Prompt 141R11 command-console pass): the page header
 * carries only the title + a single scope chip + the refresh timestamp -
 * Total/Normal/Warning/Abnormal/Unknown live in their own slim status strip
 * beneath it (dots + numbers, no card boxes), never as pills crowding the
 * title. The toolbar is one row of only the high-frequency controls: search,
 * a one-click status quick filter (segmented pills, faster than opening a
 * panel for the single most common narrowing action), page density, "ตัวกรอง
 * เพิ่มเติม", รีเฟรช, ล้างตัวกรอง when active. Super Agent Org/PLC/Agent ID
 * (advanced, code-only - no name-lookup endpoint exists) and worker stage
 * live inside the "ตัวกรองเพิ่มเติม" panel, which is a real anchored popover
 * (dismissible by outside click or Escape), not an always-full-width inline
 * block. Active filters render as small removable chips below the toolbar,
 * with Super Agent/PLC/Agent chips resolved to the real name already
 * present on the returned rows (never the raw id an operator typed). There
 * is no always-visible right-side panel - the only right-side panel is the
 * detail drawer opened by clicking a seat.
 *
 * Scale: the underlying tables can hold 200,000+ people. This page NEVER
 * loads more than one page into React state at a time, never loops over
 * pages to pre-compute a global count, and never renders more DOM seats
 * than the current page holds. pageSize defaults to WAR_ROOM_DEFAULT_PAGE_SIZE
 * (5000, the real WarRoomService.MaxPageSize ceiling - large enough that the
 * current seeded scope of 2,500 people fits on a single page) and is operator-
 * selectable among WAR_ROOM_PAGE_SIZE_OPTIONS, never higher than that real
 * server cap - a large page is read via the board's own internal scroll,
 * never by shrinking seats until they are unreadable. `totalCount` and the
 * four `summary` counts are both real, server-computed aggregates over the
 * caller's FULL filtered scope (a real SQL COUNT(*)/GROUP BY), never derived
 * from only the current page's rows.
 *
 * Pagination controls render only once `totalCount` actually exceeds
 * `pageSize` - the common case (current seeded scope, 2,500 <= 5,000) has
 * exactly one page, so a "หน้า 1 จาก 1" indicator and disabled Prev/Next
 * controls never appear as paging theater for paging that can never happen.
 *
 * Scope (SuperAdmin sees all; SuperAgent/PLC/Agent see only their own
 * branch) is resolved entirely server-side from the caller's session token
 * (WarRoomService.ResolveScope) - this page never constructs or injects an
 * org/agent id to claim scope; the filters below are optional narrowing
 * filters an operator can type in, always ANDed with (never replacing) the
 * server's own scope resolution.
 */
export default function WarRoomPage() {
  const { data: identity } = useBackofficeIdentity();
  const primaryRole = identity?.primaryRole;
  const { googleMapsApiKey } = useRuntimeConfig();

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [superAgentOrgInput, setSuperAgentOrgInput] = useState("");
  const [plcOrgInput, setPlcOrgInput] = useState("");
  const [agentIdInput, setAgentIdInput] = useState("");
  const [statusCategoryFilter, setStatusCategoryFilter] = useState("");
  const [applicationStatusFilter, setApplicationStatusFilter] = useState("");
  const [workerStatusFilter, setWorkerStatusFilter] = useState("");
  const [sortChoice, setSortChoice] = useState("name");
  const [viewMode, setViewMode] = useState(DEFAULT_VIEW_MODE);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(WAR_ROOM_DEFAULT_PAGE_SIZE);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  const [selectedPerson, setSelectedPerson] = useState(null); // { personId, personType } | null
  const [detailState, setDetailState] = useState({ status: "idle", data: null, error: null, forPersonId: null });
  const [detailReloadToken, setDetailReloadToken] = useState(0);

  const filterAnchorRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Prompt 141R11 Part C: the advanced filter panel is a real anchored
  // popover now, not an always-full-width inline block - so it needs the
  // dismiss behavior every popover in this app's category (Linear/Notion-
  // style command surfaces) already has: click outside or press Escape and
  // it closes. A click on the toggle button itself stays inside
  // filterAnchorRef, so this never fights that button's own onClick toggle.
  useEffect(() => {
    if (!filterPanelOpen) {
      return undefined;
    }

    function handleOutsideClick(event) {
      if (filterAnchorRef.current && !filterAnchorRef.current.contains(event.target)) {
        setFilterPanelOpen(false);
      }
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setFilterPanelOpen(false);
      }
    }

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [filterPanelOpen]);

  useEffect(() => {
    setPage(1);
  }, [search, superAgentOrgInput, plcOrgInput, agentIdInput, statusCategoryFilter, applicationStatusFilter, workerStatusFilter, sortChoice, pageSize]);

  const activeSort = SORT_OPTIONS.find((option) => option.value === sortChoice) ?? SORT_OPTIONS[0];

  // Deliberately no default statusCategory value here - War Room's default
  // view is every scoped person, not an abnormal-only feed (the caller can
  // still narrow to warning/abnormal/unknown explicitly via the filter
  // below, a real, server-applied query param). sortBy/sortDirection default
  // to WAR_ROOM_DEFAULT_SORT_BY/WAR_ROOM_DEFAULT_SORT_DIRECTION ("name"/"asc")
  // until the operator picks a different "การเรียง" option.
  const filters = useMemo(
    () => ({
      page,
      pageSize,
      search,
      superAgentOrganizationId: superAgentOrgInput,
      plcOrganizationId: plcOrgInput,
      agentId: agentIdInput,
      statusCategory: statusCategoryFilter,
      applicationStatus: applicationStatusFilter,
      workerStatus: workerStatusFilter,
      sortBy: activeSort.sortBy ?? WAR_ROOM_DEFAULT_SORT_BY,
      sortDirection: activeSort.sortDirection ?? WAR_ROOM_DEFAULT_SORT_DIRECTION,
    }),
    [
      page,
      pageSize,
      search,
      superAgentOrgInput,
      plcOrgInput,
      agentIdInput,
      statusCategoryFilter,
      applicationStatusFilter,
      workerStatusFilter,
      activeSort,
    ]
  );

  const { status: listStatus, data: listData, error: listError, refetch: refetchList } = useWarRoomWorkers(filters);

  useEffect(() => {
    if (listData) {
      setLastRefreshedAt(new Date());
    }
  }, [listData]);

  // Detail is fetched only once a seat is actually clicked - never
  // prefetched for every seat on the board.
  useEffect(() => {
    if (!selectedPerson) {
      setDetailState({ status: "idle", data: null, error: null, forPersonId: null });
      return undefined;
    }

    let cancelled = false;
    setDetailState((previous) => ({
      status: "loading",
      data: previous.forPersonId === selectedPerson.personId ? previous.data : null,
      error: null,
      forPersonId: selectedPerson.personId,
    }));

    getWarRoomPersonDetail(selectedPerson.personId, selectedPerson.personType)
      .then((data) => {
        if (!cancelled) {
          setDetailState({ status: "ready", data, error: null, forPersonId: selectedPerson.personId });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setDetailState({ status: "error", data: null, error, forPersonId: selectedPerson.personId });
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- detailReloadToken intentionally re-triggers a fetch for the same selectedPerson
  }, [selectedPerson, detailReloadToken]);

  // Stable identity: the map view's own "step through the watch list" timer
  // depends on this callback, and a new function every render would restart
  // that interval on every render.
  const openPersonDetail = useCallback((item) => {
    setSelectedPerson({ personId: item.personId, personType: item.personType });
  }, []);

  function closeDrawer() {
    setSelectedPerson(null);
  }

  function handleRefresh() {
    refetchList();
    if (selectedPerson) {
      setDetailReloadToken((token) => token + 1);
    }
  }

  // "ขั้นตอนแรงงาน" is the single operator-facing control for both real
  // underlying dimensions (applicationStatus/workerStatus) - Prompt 141R11
  // removed the old standalone "สถานะ Worker" section (it rendered the raw
  // backend enum value "ProfileCreated" as visible option text, exactly the
  // "no raw technical labels" violation this packet's owner flagged) since
  // this handler already fully covers the one real choice that section ever
  // exposed ("Worker Profile"). Kept mutually exclusive because the real
  // data is: a person is EITHER a WorkerApplication (has applicationStatus)
  // OR a WorkerProfile (has workerStatus), never both at once.
  function handleWorkerStageChange(value) {
    if (value === "WorkerProfile") {
      setApplicationStatusFilter("");
      setWorkerStatusFilter("ProfileCreated");
    } else {
      setApplicationStatusFilter(value);
      setWorkerStatusFilter("");
    }
  }

  const workerStageValue = workerStatusFilter === "ProfileCreated" ? "WorkerProfile" : applicationStatusFilter;

  function clearAllFilters() {
    setSearchInput("");
    setSearch("");
    setStatusCategoryFilter("");
    setApplicationStatusFilter("");
    setWorkerStatusFilter("");
    setSuperAgentOrgInput("");
    setPlcOrgInput("");
    setAgentIdInput("");
  }

  // Memoized deliberately: `listData?.items ?? []` hands out a BRAND NEW empty
  // array on every render while the list is empty/absent, and the map view
  // derives its point set (and its map-framing effect) from this identity. An
  // effect that re-runs every render, moves the map, and so causes the next
  // render is the exact CPU-pegging loop this codebase has hit before.
  const items = useMemo(() => listData?.items ?? [], [listData]);
  const totalCount = listData?.totalCount ?? 0;
  const summary = listData?.summary ?? { total: 0, normal: 0, warning: 0, abnormal: 0, unknown: 0 };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, totalCount);
  // War Room pages as little as possible: when the caller's entire scoped
  // count already fits inside one request (the common case - pageSize
  // defaults to the real 5000 server cap, comfortably above the current
  // seeded scope), there is only ever one page, and showing "หน้า 1 จาก 1"
  // plus disabled Prev/Next controls would just be paging UI for paging
  // that can never happen. Pagination only renders at all once the server's
  // own totalCount proves a second page is real.
  const hasMultiplePages = totalCount > pageSize;

  const activeFilterChips = useMemo(() => {
    const chips = [];
    if (search) {
      chips.push({ key: "search", label: `ค้นหา: ${search}`, onRemove: () => setSearchInput("") || setSearch("") });
    }
    if (statusCategoryFilter) {
      chips.push({
        key: "statusCategory",
        label: `สถานะ: ${STATUS_CATEGORY_LABEL_TH[statusCategoryFilter] ?? statusCategoryFilter}`,
        onRemove: () => setStatusCategoryFilter(""),
      });
    }
    if (workerStageValue) {
      const stageOption = WORKER_STAGE_OPTIONS.find((option) => option.value === workerStageValue);
      chips.push({
        key: "workerStage",
        label: `ขั้นตอน: ${stageOption?.label ?? workerStageValue}`,
        onRemove: () => handleWorkerStageChange(""),
      });
    }
    // Prompt 141R11: a chip must read as a real person/org name, never the
    // raw id an operator typed in - items[0] is the same already-scoped,
    // already-fetched result the board itself renders (the exact pattern
    // boardLabel below already established for Agent), so resolving the
    // chip label costs no extra request and is never fabricated. Falls back
    // to the raw id only while a request is in flight or a typo'd id
    // matches nothing yet - still honest, never a placeholder name.
    if (superAgentOrgInput) {
      chips.push({
        key: "superAgentOrg",
        label: `Super Agent: ${items[0]?.superAgentName ?? superAgentOrgInput}`,
        onRemove: () => setSuperAgentOrgInput(""),
      });
    }
    if (plcOrgInput) {
      chips.push({ key: "plcOrg", label: `PLC: ${items[0]?.plcName ?? plcOrgInput}`, onRemove: () => setPlcOrgInput("") });
    }
    if (agentIdInput) {
      chips.push({ key: "agentId", label: `Agent: ${items[0]?.agentName ?? agentIdInput}`, onRemove: () => setAgentIdInput("") });
    }
    return chips;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onRemove closures intentionally read the latest setters, not re-derived from a dependency list
  }, [search, statusCategoryFilter, workerStageValue, superAgentOrgInput, plcOrgInput, agentIdInput, items]);

  const hasActiveFilters = activeFilterChips.length > 0;
  // Scoped to the filters that live ONLY inside the advanced panel - status
  // already has its own always-visible quick-filter pill in the main
  // toolbar (Prompt 141R11 Part C), so counting it here too would double-
  // signal the same active filter in two places at once.
  const advancedFilterCount = [workerStageValue, superAgentOrgInput, plcOrgInput, agentIdInput].filter(Boolean).length;

  // The board is a single flat list by default - Agent is a narrowing
  // filter, never a default grouping. The board's own label only ever names
  // a specific Agent when the caller has actually filtered by one (every
  // returned row necessarily shares that Agent then); otherwise it stays
  // the honest "all scoped people" label.
  const boardLabel = agentIdInput && items[0]?.agentName ? items[0].agentName : ALL_SCOPE_LABEL;

  const [boardBodyRef, boardBodyWidth] = useContainerWidth();
  const seatColumns = useMemo(() => computeSeatColumns(boardBodyWidth), [boardBodyWidth]);

  // Prompt 141R14 Part B point 3: a bucket that needs more than one visual
  // row gets numbered sub-labels (A1/A2/...) so each row still reads as
  // "which letter, which part of it" rather than repeating the bare letter
  // on every row. A bucket that fits in a single row keeps the plain
  // letter label unchanged (no "A1" for a bucket that's never split) -
  // this is still the same real alphabetical bucketing
  // (buildAlphabeticalSeatRows) as before, just labeled more precisely when
  // a letter overflows one row.
  const seatRows = useMemo(() => {
    const buckets = buildAlphabeticalSeatRows(items);
    const rows = [];
    for (const bucket of buckets) {
      const bucketRows = chunkIntoRows(bucket.items, seatColumns);
      const needsSubLabel = bucketRows.length > 1;
      bucketRows.forEach((seats, indexInBucket) => {
        const label = needsSubLabel ? `${bucket.label}${indexInBucket + 1}` : bucket.label;
        rows.push({ key: `${bucket.label}-${indexInBucket}`, label, seats });
      });
    }
    return rows;
  }, [items, seatColumns]);

  const isInitialLoading = listStatus === "loading" && listData === null;
  const isInitialError = listStatus === "error" && listData === null;

  if (isInitialLoading) {
    return (
      <AppShell crumbs={CRUMBS}>
        <PageSkeleton variant="board" label="กำลังโหลด War Room..." />
      </AppShell>
    );
  }

  if (isInitialError) {
    return (
      <AppShell crumbs={CRUMBS}>
        <div className="wx-state-card wx-state-card--danger" role="alert">
          <h1 className="wx-state-title">ไม่สามารถโหลด War Room ได้</h1>
          <p className="wx-state-body">{describeListError(listError)}</p>
          <button type="button" className="wx-button wx-button--primary" onClick={handleRefresh}>
            ลองใหม่อีกครั้ง
          </button>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell crumbs={CRUMBS}>
      <div className="wx-wr-page">
        <header className="wx-wr-header">
          <div className="wx-wr-header-left">
            <h1 className="wx-wr-title">
              War Room <span className="wx-wr-title-th">ห้องเฝ้าระวังปฏิบัติการ</span>
            </h1>
          </div>
          <div className="wx-wr-header-right">
            {primaryRole && <span className="wx-wr-scope-chip">Scope {primaryRole}</span>}
            <span className="wx-wr-refreshed-at">{lastRefreshedAt ? `อัปเดตล่าสุด ${formatTimeOnlyTh(lastRefreshedAt)}` : "ยังไม่เคยอัปเดต"}</span>
          </div>
        </header>

        <div className="wx-wr-status-strip" role="group" aria-label="สรุปสถานะ">
          <span className="wx-wr-status-item wx-wr-status-item--total">
            Total <b>{summary.total.toLocaleString("th-TH")}</b>
          </span>
          <span className="wx-wr-status-sep" aria-hidden="true" />
          <span className="wx-wr-status-item">
            <span className="wx-wr-dot wx-wr-dot--normal" aria-hidden="true" /> Normal <b>{summary.normal.toLocaleString("th-TH")}</b>
          </span>
          <span className="wx-wr-status-sep" aria-hidden="true" />
          <span className="wx-wr-status-item">
            <span className="wx-wr-dot wx-wr-dot--warning" aria-hidden="true" /> Warning <b>{summary.warning.toLocaleString("th-TH")}</b>
          </span>
          <span className="wx-wr-status-sep" aria-hidden="true" />
          <span className="wx-wr-status-item">
            <span className="wx-wr-dot wx-wr-dot--abnormal" aria-hidden="true" /> Abnormal <b>{summary.abnormal.toLocaleString("th-TH")}</b>
          </span>
          <span className="wx-wr-status-sep" aria-hidden="true" />
          <span className="wx-wr-status-item">
            <span className="wx-wr-dot wx-wr-dot--neutral" aria-hidden="true" /> Unknown <b>{summary.unknown.toLocaleString("th-TH")}</b>
          </span>
        </div>

        <div className="wx-wr-toolbar">
          {/* View switch first in the row: it changes what the whole page is,
              so it reads before the controls that narrow what is in it. Both
              views render the same filtered result - switching never changes
              the query. */}
          <div className="wx-wr-viewswitch" role="group" aria-label="มุมมอง">
            {VIEW_MODES.map((mode) => (
              <button
                key={mode.value}
                type="button"
                className={`wx-wr-viewswitch-btn${viewMode === mode.value ? " wx-wr-viewswitch-btn--active" : ""}`}
                aria-pressed={viewMode === mode.value}
                onClick={() => setViewMode(mode.value)}
              >
                <mode.Icon size={14} aria-hidden="true" />
                {mode.label}
              </button>
            ))}
          </div>

          <label className="wx-wr-toolbar-search">
            <span className="wx-visually-hidden">ค้นหาชื่อแรงงาน</span>
            <input type="search" placeholder="ค้นหาชื่อแรงงาน" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} />
          </label>

          {/* Prompt 141R11 Part C: status is a one-click quick filter here -
              faster than opening the advanced panel for the single most
              common narrowing action - rather than a <select> that costs a
              click to open plus a click to choose. */}
          <div className="wx-wr-status-quickfilter" role="group" aria-label="กรองตามสถานะ">
            <button
              type="button"
              className={`wx-wr-status-quickfilter-btn${statusCategoryFilter === "" ? " wx-wr-status-quickfilter-btn--active" : ""}`}
              aria-pressed={statusCategoryFilter === ""}
              onClick={() => setStatusCategoryFilter("")}
            >
              ทั้งหมด
            </button>
            {STATUS_CATEGORY_OPTIONS.map((status) => (
              <button
                key={status}
                type="button"
                className={`wx-wr-status-quickfilter-btn${statusCategoryFilter === status ? " wx-wr-status-quickfilter-btn--active" : ""}`}
                aria-pressed={statusCategoryFilter === status}
                onClick={() => setStatusCategoryFilter(status)}
              >
                <span className={`wx-wr-dot wx-wr-dot--${status === "unknown" ? "neutral" : status}`} aria-hidden="true" />
                {STATUS_CATEGORY_LABEL_TH[status]}
              </button>
            ))}
          </div>

          <select
            className="wx-wr-toolbar-select"
            aria-label="จำนวนต่อหน้า"
            value={pageSize}
            onChange={(event) => setPageSize(Number(event.target.value))}
          >
            {WAR_ROOM_PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}/หน้า
              </option>
            ))}
          </select>

          <div className="wx-wr-filter-anchor" ref={filterAnchorRef}>
            <button
              type="button"
              className={`wx-button wx-button--ghost wx-wr-toolbar-button wx-wr-filter-button${advancedFilterCount > 0 ? " wx-wr-filter-button--active" : ""}`}
              aria-expanded={filterPanelOpen}
              onClick={() => setFilterPanelOpen((open) => !open)}
            >
              <Filter size={15} aria-hidden="true" /> ตัวกรองเพิ่มเติม
              {advancedFilterCount > 0 && (
                <span className="wx-wr-filter-badge" aria-hidden="true">
                  {advancedFilterCount}
                </span>
              )}
            </button>

            {filterPanelOpen && (
              <div className="wx-wr-filter-panel" role="dialog" aria-label="ตัวกรองเพิ่มเติม">
                <div className="wx-wr-filter-section">
                  <h3>สถานะ</h3>
                  <select aria-label="สถานะ" value={statusCategoryFilter} onChange={(event) => setStatusCategoryFilter(event.target.value)}>
                    <option value="">ทั้งหมด</option>
                    {STATUS_CATEGORY_OPTIONS.map((status) => (
                      <option key={status} value={status}>
                        {STATUS_CATEGORY_LABEL_TH[status]}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="wx-wr-filter-section">
                  <h3>ขั้นตอนแรงงาน</h3>
                  <select aria-label="ขั้นตอนแรงงาน" value={workerStageValue} onChange={(event) => handleWorkerStageChange(event.target.value)}>
                    {WORKER_STAGE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="wx-wr-filter-section wx-wr-filter-section--advanced">
                  <h3>สายงาน</h3>
                  <p className="wx-wr-filter-advanced-note">ขั้นสูง - กรอกรหัสเมื่อทราบแน่ชัด (Agent เป็นเพียงตัวกรอง ไม่ใช่การจัดกลุ่มเริ่มต้นของบอร์ด)</p>
                  <label className="wx-wr-filter-field">
                    <span>รหัส (ขั้นสูง) - Super Agent Org</span>
                    <input type="text" placeholder="รหัส" value={superAgentOrgInput} onChange={(event) => setSuperAgentOrgInput(event.target.value)} />
                  </label>
                  <label className="wx-wr-filter-field">
                    <span>รหัส (ขั้นสูง) - PLC / บมจ</span>
                    <input type="text" placeholder="รหัส" value={plcOrgInput} onChange={(event) => setPlcOrgInput(event.target.value)} />
                  </label>
                  <label className="wx-wr-filter-field">
                    <span>รหัส (ขั้นสูง) - Agent</span>
                    <input type="text" placeholder="รหัส" value={agentIdInput} onChange={(event) => setAgentIdInput(event.target.value)} />
                  </label>
                </div>

                <div className="wx-wr-filter-section">
                  <h3>การเรียง</h3>
                  <select aria-label="การเรียง" value={sortChoice} onChange={(event) => setSortChoice(event.target.value)}>
                    {SORT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="wx-wr-filter-section">
                  <h3>จำนวนต่อหน้า</h3>
                  <select
                    aria-label="จำนวนต่อหน้า (ตัวกรองเพิ่มเติม)"
                    value={pageSize}
                    onChange={(event) => setPageSize(Number(event.target.value))}
                  >
                    {WAR_ROOM_PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>

          <button type="button" className="wx-button wx-button--ghost wx-wr-toolbar-button wx-wr-refresh-button" onClick={handleRefresh}>
            <RefreshCw size={15} aria-hidden="true" /> รีเฟรช
          </button>
          {hasActiveFilters && (
            <button type="button" className="wx-button wx-button--ghost wx-wr-toolbar-button wx-wr-clear-filters-button" onClick={clearAllFilters}>
              ล้างตัวกรอง
            </button>
          )}
        </div>

        {hasActiveFilters && (
          <div className="wx-wr-active-filters" aria-label="ตัวกรองที่ใช้งานอยู่">
            {activeFilterChips.map((chip) => (
              <span key={chip.key} className="wx-wr-active-filter-chip">
                {chip.label}
                <button type="button" aria-label={`ลบตัวกรอง ${chip.label}`} onClick={chip.onRemove}>
                  ×
                </button>
              </span>
            ))}
            <button type="button" className="wx-wr-clear-filters-inline" onClick={clearAllFilters}>
              ล้างตัวกรอง
            </button>
          </div>
        )}

        {totalCount === 0 ? (
          <div className="wx-wr-empty-state">
            <h2>{hasActiveFilters ? "ไม่พบแรงงานที่ตรงกับตัวกรองหรือคำค้นหา" : "ยังไม่มีข้อมูลแรงงานในขอบเขตที่มองเห็น"}</h2>
            <p>{hasActiveFilters ? "ลองล้างตัวกรองหรือแก้คำค้นหา" : "เมื่อมีแรงงาน/ผู้สมัครในขอบเขตของคุณ จะแสดงเป็นที่นั่งในบอร์ดนี้ทันที ไม่ต้องรอ heartbeat"}</p>
          </div>
        ) : viewMode === "map" ? (
          <div className="wx-wr-board-col">
            <Suspense fallback={<MediaSkeleton label="กำลังโหลดแผนที่..." height={560} />}>
              <WarRoomMapPanel
                items={items}
                totalCount={totalCount}
                selectedPerson={selectedPerson}
                onSelectPerson={openPersonDetail}
                boardLabel={boardLabel}
                lastRefreshedAt={lastRefreshedAt}
                onRefresh={handleRefresh}
              />
            </Suspense>

            {/* The map draws this page's people, so paging still belongs to
                it - the same control, in the same place, as on the board. */}
            {hasMultiplePages && (
              <Pagination page={page} totalPages={totalPages} totalCount={totalCount} pageSize={pageSize} onPageChange={setPage} />
            )}

            {listStatus === "error" && listData !== null && (
              <p className="wx-wr-inline-error" role="alert">
                {describeListError(listError)}
              </p>
            )}
          </div>
        ) : (
          <div className="wx-wr-board-col">
            <section className="wx-wr-board-panel wx-wr-board-panel--light" data-testid="war-room-seating-board">
              <div className="wx-wr-board-head">
                <div className="wx-wr-board-head-left">
                  <strong className="wx-wr-board-label">{boardLabel}</strong>
                </div>
                <span className="wx-wr-page-indicator">
                  {hasMultiplePages
                    ? `หน้า ${page} จาก ${totalPages} · แสดง ${rangeStart.toLocaleString("th-TH")}-${rangeEnd.toLocaleString("th-TH")} จาก ${totalCount.toLocaleString("th-TH")}`
                    : `${items.length.toLocaleString("th-TH")} คนในหน้านี้`}
                </span>
              </div>

              {/* Prompt 141R11 Part D: the Normal/Warning/Abnormal/Unknown
                  color key already lives in two other places (the status
                  strip above, with real counts, and the quick-filter pills'
                  own dots) - a third plain legend here would just repeat the
                  same four dots a third time without adding information. */}
              <div className="wx-wr-board-body wx-wr-board-body--readable" ref={boardBodyRef}>
                {seatRows.map((row, rowIndex) => (
                  <div key={row.key} className={`wx-wr-seat-row ${rowIndex % 2 === 0 ? "wx-wr-seat-row--even" : "wx-wr-seat-row--odd"}`}>
                    <span className="wx-wr-row-label" aria-hidden="true">
                      {row.label}
                    </span>
                    <div className="wx-wr-seats wx-wr-seats--dense">
                      {row.seats.map((item) => {
                        const status =
                          item.statusCategory === "abnormal"
                            ? "abnormal"
                            : item.statusCategory === "warning"
                              ? "warning"
                              : item.statusCategory === "normal"
                                ? "normal"
                                : "neutral";
                        const isSelected = selectedPerson?.personId === item.personId && selectedPerson?.personType === item.personType;
                        const label = `${item.displayName ?? "-"} · ${item.reasonLabel}`;
                        return (
                          <button
                            key={`${item.personType}:${item.personId}`}
                            type="button"
                            className={`wx-wr-seat wx-wr-seat--${status}${status === "abnormal" ? " wx-wr-seat--blink" : ""}${isSelected ? " wx-wr-seat--selected" : ""}`}
                            title={label}
                            aria-label={label}
                            aria-pressed={isSelected}
                            onClick={() => openPersonDetail(item)}
                          >
                            <span className={`wx-wr-seat-avatar${status === "abnormal" ? " wx-wr-seat-avatar--blink" : ""}`} aria-hidden="true">
                              <UserRound size={14} strokeWidth={2.25} />
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {hasMultiplePages && (
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  totalCount={totalCount}
                  pageSize={pageSize}
                  onPageChange={setPage}
                />
              )}
            </section>

            {listStatus === "error" && listData !== null && (
              <p className="wx-wr-inline-error" role="alert">
                {describeListError(listError)}
              </p>
            )}
          </div>
        )}

        <footer className="wx-wr-footer">
          <span className="wx-wr-about" tabIndex={0} title={ABOUT_TOOLTIP}>
            เกี่ยวกับข้อมูลนี้
          </span>
        </footer>
      </div>

      {/* The whole map surface counts as "inside" for the drawer's outside-click
          check: clicking a dot, a watch-rail row, or just panning the map must
          not close the drawer the operator is reading. */}
      <DetailDrawer
        open={selectedPerson !== null}
        onClose={closeDrawer}
        labelledBy={DRAWER_TITLE_ID}
        selectableSelector=".wx-wr-seat, .wx-wr-refresh-button, .wx-wrm-shell"
      >
        <PersonDetailView titleId={DRAWER_TITLE_ID} state={detailState} apiKey={googleMapsApiKey} />
      </DetailDrawer>
    </AppShell>
  );
}

/**
 * P141R17: the War Room drawer's own Tracking/Heartbeat mini map - thin
 * wrapper over the shared GoogleMiniMap (real Google Maps JS API when
 * `apiKey` is configured, a real keyless Google Maps embed for this single
 * coordinate otherwise). No Leaflet/OpenStreetMap anywhere in this surface
 * anymore.
 */
function MiniLocationMap({ latitude, longitude, apiKey }) {
  return <GoogleMiniMap latitude={latitude} longitude={longitude} apiKey={apiKey} />;
}

/**
 * A real, modal overlay showing the actual saved passport/identity document
 * image for `applicationId` (the WorkerApplication's own id, or - for an
 * already-promoted WorkerProfile person - that profile's real
 * sourceApplicationId) via the real authenticated
 * GET .../passport-document/file endpoint (P141R16). Never shows a storage
 * key/file path - the fetch is keyed by applicationId only, and the
 * component only ever renders real returned bytes or an honest "no document"
 * empty state (a 404 ApiClientError), never a placeholder image.
 */
function PassportModal({ applicationId, workerName, passportNumberMasked, onClose }) {
  const [state, setState] = useState({ status: "loading", objectUrl: null, error: null });

  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;
    setState({ status: "loading", objectUrl: null, error: null });
    getWorkerApplicationPassportDocumentBlob(applicationId)
      .then(({ blob }) => {
        if (cancelled) {
          return;
        }
        createdUrl = URL.createObjectURL(blob);
        setState({ status: "ready", objectUrl: createdUrl, error: null });
      })
      .catch((error) => {
        if (!cancelled) {
          setState({ status: "error", objectUrl: null, error });
        }
      });
    return () => {
      cancelled = true;
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
      }
    };
  }, [applicationId]);

  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === "Escape") {
        onClose();
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <div className="wx-wr-passport-modal-backdrop" onClick={onClose}>
      <div
        className="wx-wr-passport-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`รูป Passport ของ ${workerName ?? "-"}`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="wx-wr-passport-modal-head">
          <div>
            <strong>{workerName ?? "-"}</strong>
            {passportNumberMasked && <span className="wx-wr-source-id"> · {passportNumberMasked}</span>}
          </div>
          <button type="button" className="wx-wr-passport-modal-close" onClick={onClose} aria-label="ปิด">
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        <div className="wx-wr-passport-modal-body">
          {state.status === "loading" && (
            <MediaSkeleton label="กำลังโหลดรูป Passport..." height={360} />
          )}
          {state.status === "error" && (
            <p className="wx-wr-note-muted wx-wr-reserved-text">
              {state.error?.status === 404 || state.error?.status === 403
                ? "ยังไม่มีรูป Passport สำหรับผู้สมัครรายนี้ในระบบ"
                : "ไม่สามารถโหลดรูป Passport ได้ กรุณาลองใหม่อีกครั้ง"}
            </p>
          )}
          {state.status === "ready" && <img src={state.objectUrl} alt={`Passport ของ ${workerName ?? "-"}`} className="wx-wr-passport-modal-image" />}
        </div>
      </div>
    </div>
  );
}

/**
 * "ข้อมูล Worker Profile" - real identity fields fetched from the already-
 * shipped worker-profiles/worker-applications detail APIs (cross-feature
 * reuse, no new endpoint), never fabricated. P141R17: dbo.WmxWorkers.PhotoUrl
 * (wired in P141R16, now actually seeded for the real P140D dataset - see
 * TestBackofficeMediaSeedEndpoints.cs) renders as a real `<img>` whenever
 * present; the initials placeholder only ever renders for a record that
 * genuinely has none. Two remaining, confirmed permanent gaps this
 * component still does not paper over:
 *   1. Neither API exposes a discrete first-name/last-name split - only
 *      fullNameNative/fullNameEnglish (combined) exist, so this shows those
 *      two real fields honestly instead of fabricating a split (a naive
 *      whitespace split would be wrong for most of this app's real names).
 *   2. The passport number is a real clickable control that opens
 *      PassportModal only when a real applicationId to look the document up
 *      by exists - never a route jump, never a fake link.
 */
function WorkerProfileSection({ personId, personType, displayName }) {
  const [state, setState] = useState({ status: "loading", data: null, error: null });
  const [passportModalOpen, setPassportModalOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading", data: null, error: null });
    setPassportModalOpen(false);
    const request = personType === "WorkerProfile" ? getWorkerProfile(personId) : getWorkerApplication(personId);
    request
      .then((data) => {
        if (!cancelled) {
          setState({ status: "ready", data, error: null });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setState({ status: "error", data: null, error });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [personId, personType]);

  const initials =
    (displayName ?? "")
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toUpperCase() || "-";

  if (state.status === "loading") {
    return (
      <section className="wx-wr-dsec">
        <h3>ข้อมูล Worker Profile</h3>
        <p className="wx-wr-note-muted wx-wr-note-muted--left">กำลังโหลด...</p>
      </section>
    );
  }

  if (state.status === "error") {
    return (
      <section className="wx-wr-dsec">
        <h3>ข้อมูล Worker Profile</h3>
        <p className="wx-wr-note-muted wx-wr-note-muted--left">ไม่สามารถโหลดข้อมูล Worker Profile ได้</p>
      </section>
    );
  }

  const data = state.data;
  const isWorkerProfile = personType === "WorkerProfile";
  const fullNameNative = isWorkerProfile ? data.worker?.fullNameNative : data.fullNameNative;
  const fullNameEnglish = isWorkerProfile ? data.worker?.fullNameEnglish : data.fullNameEnglish;
  const nationality = isWorkerProfile ? data.worker?.nationality : data.nationality;
  // P141R16: dbo.WmxWorkers.PhotoUrl now really reaches this DTO (see
  // WorkerProfileWorkerDto.PhotoUrl) - real image if the record has one,
  // never invented. WorkerApplication has no photo field anywhere.
  const photoUrl = isWorkerProfile ? (data.worker?.photoUrl ?? null) : null;
  // Both branches now carry a real masked passport number (last 4 visible) -
  // WorkerProfileWorkerDto.PassportNumberMasked (P141R16) for a promoted
  // worker, WorkerApplicationDetailDto.PassportNumberMasked for a not-yet-
  // promoted one.
  const passportNumberMasked = isWorkerProfile ? (data.worker?.passportNumberMasked ?? null) : (data.passportNumberMasked ?? null);
  // The real application id whose stored document (if any) this person's
  // passport traces back to - the application itself when personType is
  // WorkerApplication, or the promoted worker's own real sourceApplicationId
  // when one exists. Passport-image lookup is only ever attempted against a
  // real id like this - never guessed.
  const passportApplicationId = isWorkerProfile ? (data.sourceApplicationId ?? null) : personId;
  const canAttemptPassportImage = Boolean(passportApplicationId);

  return (
    <section className="wx-wr-dsec">
      <h3>ข้อมูล Worker Profile</h3>
      <div className="wx-wr-profile-photo-row">
        {photoUrl ? (
          <img src={photoUrl} alt={displayName ?? "worker"} className="wx-wr-profile-photo-image" />
        ) : (
          <span className="wx-wr-profile-photo" aria-hidden="true">
            {initials}
          </span>
        )}
        {!photoUrl && <span className="wx-wr-note-muted wx-wr-reserved-text">ยังไม่มีรูปโปรไฟล์</span>}
      </div>
      <div className="wx-wr-kv-row">
        <span>ชื่อเต็ม (ไทย/พื้นเมือง)</span>
        <strong className={fullNameNative ? undefined : "wx-wr-reserved-text"}>{fullNameNative ?? "ไม่มีข้อมูล"}</strong>
      </div>
      <div className="wx-wr-kv-row">
        <span>ชื่อเต็ม (อังกฤษ)</span>
        <strong className={fullNameEnglish ? undefined : "wx-wr-reserved-text"}>{fullNameEnglish ?? "ไม่มีข้อมูล"}</strong>
      </div>
      <div className="wx-wr-kv-row">
        <span>สัญชาติ</span>
        <strong className={nationality ? undefined : "wx-wr-reserved-text"}>{nationality ?? "ไม่ระบุ"}</strong>
      </div>
      <div className="wx-wr-kv-row">
        <span>หมายเลข Passport</span>
        {passportNumberMasked && canAttemptPassportImage ? (
          <button type="button" className="wx-wr-passport-link" onClick={() => setPassportModalOpen(true)}>
            {passportNumberMasked}
          </button>
        ) : (
          <strong className="wx-wr-reserved-text">{passportNumberMasked ?? "ไม่มีข้อมูลในระบบ"}</strong>
        )}
      </div>
      {passportModalOpen && (
        <PassportModal
          applicationId={passportApplicationId}
          workerName={displayName}
          passportNumberMasked={passportNumberMasked}
          onClose={() => setPassportModalOpen(false)}
        />
      )}
    </section>
  );
}

function PersonDetailView({ titleId, state, apiKey }) {
  // P159: owned here rather than by the page, so closing the drawer and opening
  // another person cannot leave a stale Footstep dialog showing the last one.
  const [footstepOpen, setFootstepOpen] = useState(false);

  if (state.status === "loading" || state.status === "idle") {
    return (
      <DetailSkeleton label="กำลังโหลดรายละเอียด..." />
    );
  }

  if (state.status === "error") {
    return (
      <div className="wx-wr-drawer-state" role="alert">
        <h2 id={titleId} className="wx-state-title">
          ไม่สามารถโหลดรายละเอียดได้
        </h2>
        <p className="wx-state-body">{describeDetailError(state.error)}</p>
      </div>
    );
  }

  const detail = state.data;
  const hasCoordinates = detail.latitude != null && detail.longitude != null;
  const hasEverTracked = detail.lastHeartbeatAtUtc != null;
  const isAbnormal = detail.statusCategory === "abnormal";
  const bandVariantClass = isAbnormal ? "wx-wr-dsec--source" : detail.statusCategory === "warning" ? "wx-wr-alert-band--warning" : "wx-wr-alert-band--neutral";
  const signals = buildDrawerSignals(detail);
  const primarySignal = signals[0] ?? null;
  const primaryAction = isAbnormal ? sourceDestination(detail.sourceType) : null;
  const lifecycleValue = lifecycleStatusValue(detail);
  const isWorkerProfile = detail.personType === "WorkerProfile";
  const hasMissingHeartbeatEvent = isAbnormal && detail.sourceType === "AbnormalEvent";

  return (
    <div className="wx-wr-drawer">
      <div className="wx-wr-drawer-sticky-header">
        <span className="wx-wr-drawer-kicker">WAR ROOM · PERSON SNAPSHOT</span>
        <div className="wx-wr-drawer-title-row">
          <h2 id={titleId} className="wx-wr-drawer-title">
            {detail.displayName ?? "-"}
          </h2>
          {detail.personId && <span className="wx-wr-drawer-code">{truncateId(detail.personId)}</span>}
        </div>
        <div className="wx-wr-drawer-status">
          <span className="wx-badge wx-badge--neutral">{lifecycleValue ?? (isWorkerProfile ? "Worker Profile" : "Worker Application")}</span>
          <span className={`wx-badge wx-badge--${heartbeatBadgeVariant(detail.statusCategory)}`}>
            {STATUS_CATEGORY_LABEL_TH[detail.statusCategory] ?? detail.statusCategory}
          </span>
        </div>
        {/* P159: the route this person walked, at the TOP of the drawer where an
            operator investigating an abnormal status looks first - the inline
            panel further down is today-only and too narrow to read a map on.
            Offered only for a promoted Worker Profile, because that is the only
            person type the footstep endpoint has data for. */}
        {isWorkerProfile && detail.personId && (
          <button type="button" className="wx-wr-drawer-footstep-cta" onClick={() => setFootstepOpen(true)}>
            <Footprints size={15} aria-hidden="true" />
            ดู Footstep
          </button>
        )}
      </div>

      <FootstepDialog
        open={footstepOpen}
        personId={detail.personId}
        displayName={detail.displayName}
        onClose={() => setFootstepOpen(false)}
      />

      <WorkerProfileSection personId={detail.personId} personType={detail.personType} displayName={detail.displayName} />

      <section className={`wx-wr-dsec wx-wr-alert-band ${bandVariantClass}`}>
        <h3>{isAbnormal ? "เหตุผลการจัดสถานะผิดปกติ" : "สถานะล่าสุด"}</h3>
        <div className="wx-wr-alert-signal">
          <strong>{isAbnormal ? formalAbnormalReasonText(detail) : detail.reasonLabel}</strong>
        </div>
        <div className="wx-wr-alert-meta">
          {hasEverTracked && <span>{formatDateTimeTh(detail.lastHeartbeatAtUtc)}</span>}
          {primarySignal && (
            <span className="wx-wr-alert-source">
              <span>{primarySignal.label}</span>
              {" · "}
              <span className="wx-wr-source-id">{detail.sourceCode ?? truncateId(primarySignal.sourceId)}</span>
            </span>
          )}
        </div>
        {primaryAction && (
          <div className="wx-wr-action-buttons">
            <Link to={primaryAction.to} className="wx-button wx-button--primary">
              {primaryAction.label}
            </Link>
          </div>
        )}
      </section>

      <section className="wx-wr-dsec">
        <h3>สายงาน / สถานะ</h3>
        <div className="wx-wr-kv-row">
          <span>Super Agent</span>
          <strong>{detail.superAgentName ?? "-"}</strong>
        </div>
        <div className="wx-wr-kv-row">
          <span>PLC / บมจ</span>
          <strong>{detail.plcName ?? "ไม่ระบุ"}</strong>
        </div>
        <div className="wx-wr-kv-row">
          <span>Agent</span>
          <strong>{detail.agentName ?? "-"}</strong>
        </div>
        <div className="wx-wr-kv-row">
          <span>{isWorkerProfile ? "สถานะ Worker Profile" : "สถานะใบสมัคร"}</span>
          <strong className={lifecycleValue ? undefined : "wx-wr-reserved-text"}>{lifecycleValue ?? "-"}</strong>
        </div>
      </section>

      <section className="wx-wr-dsec">
        <h3>Tracking / Heartbeat</h3>
        {hasEverTracked ? (
          <>
            <div className="wx-wr-kv-row">
              <span>Heartbeat ล่าสุด</span>
              <strong>{formatDateTimeTh(detail.lastHeartbeatAtUtc)}</strong>
            </div>
            <div className="wx-wr-kv-row">
              <span>ตำแหน่งล่าสุด (เวลา)</span>
              <strong className={detail.lastLocationAtUtc ? undefined : "wx-wr-reserved-text"}>
                {formatDateTimeTh(detail.lastLocationAtUtc) ?? "ยังไม่มีข้อมูลตำแหน่ง"}
              </strong>
            </div>
            {hasCoordinates ? (
              <div className="wx-wr-coord-box">
                <span className="wx-wr-coord-box-label">ตำแหน่งล่าสุด</span>
                <div className="wx-wr-coord-pair">
                  <div>
                    <span>Latitude</span>
                    <strong>{detail.latitude}</strong>
                  </div>
                  <div>
                    <span>Longitude</span>
                    <strong>{detail.longitude}</strong>
                  </div>
                </div>
                <MiniLocationMap latitude={detail.latitude} longitude={detail.longitude} apiKey={apiKey} />
              </div>
            ) : (
              <>
                <p className="wx-wr-note-muted wx-wr-note-muted--left">ยังไม่มีตำแหน่งล่าสุด</p>
                <MiniLocationMap latitude={null} longitude={null} apiKey={apiKey} />
              </>
            )}
          </>
        ) : (
          <p className="wx-wr-note-muted wx-wr-note-muted--left wx-wr-reserved-text">
            {isWorkerProfile ? "ยังไม่เริ่ม Tracking / รอเปิดแอป - อุปกรณ์ยังไม่เคยส่ง heartbeat" : "ยังไม่มี Worker Profile จึงยังไม่มี Tracking"}
          </p>
        )}
      </section>

      {hasMissingHeartbeatEvent && (
        <section className="wx-wr-dsec wx-wr-dsec--source">
          <h3>เหตุ Missing Heartbeat ที่เกี่ยวข้อง</h3>
          <div className="wx-wr-kv-row">
            <span>อ้างอิง</span>
            <strong className="wx-wr-source-id">{detail.sourceCode ?? truncateId(detail.sourceId)}</strong>
          </div>
          <div className="wx-wr-kv-row">
            <span>เวลาที่พบเหตุ</span>
            <strong className={detail.sourceEventAtUtc ? undefined : "wx-wr-reserved-text"}>
              {formatDateTimeTh(detail.sourceEventAtUtc) ?? "ไม่มีข้อมูล"}
            </strong>
          </div>
          <div className="wx-wr-kv-row">
            <span>สถานะ</span>
            <strong className={detail.sourceStatus ? undefined : "wx-wr-reserved-text"}>{detail.sourceStatus ?? "ไม่มีข้อมูล"}</strong>
          </div>
          <div className="wx-wr-kv-row">
            <span>แหล่งที่มา</span>
            <strong>Missing Heartbeat</strong>
          </div>
        </section>
      )}

      <section className="wx-wr-dsec">
        <h3>อุปกรณ์</h3>
        <div className="wx-wr-kv-row">
          <span>แบตเตอรี่</span>
          <strong className={detail.batteryLevel != null ? undefined : "wx-wr-reserved-text"}>
            {detail.batteryLevel != null ? `${detail.batteryLevel}%` : "ไม่มีข้อมูล"}
          </strong>
        </div>
        <div className="wx-wr-kv-row">
          <span>เครือข่าย</span>
          <strong className={detail.networkType ? undefined : "wx-wr-reserved-text"}>{detail.networkType ?? "ไม่ทราบ"}</strong>
        </div>
        <div className="wx-wr-kv-row">
          <span>เวอร์ชันแอป</span>
          <strong className={detail.appVersion ? undefined : "wx-wr-reserved-text"}>{detail.appVersion ?? "ไม่ระบุ"}</strong>
        </div>
        <div className="wx-wr-kv-row">
          <span>การผูกอุปกรณ์</span>
          <strong>
            <span className="wx-badge wx-badge--info">{detail.deviceBindingStatus ?? "ไม่ระบุ"}</span>
          </strong>
        </div>
      </section>

      <p className="wx-wr-note-muted wx-wr-note-muted--left wx-wr-reserved-text wx-wr-unavailable-note">
        Wi-Fi / Cellular Fingerprint และ Push/SMS/LINE ที่ส่งจริง: ยังไม่มีระบบ
      </p>
    </div>
  );
}
